// Vendor
import {
    AfterViewInit,
    Component,
    effect,
    ElementRef,
    model,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
} from '@angular/core';
import { finalize } from 'rxjs/operators';

// Constants
import { INITIAL_COMPLIANCE_DESIGNATION_TYPE_FILTER_STATE } from 'FoldersApp/apps/compliance-report-history-app/state/compliance-designation-type/compliance-designation-type.reducer';
import { INITIAL_COMPLIANCE_REPORT_TYPE_FILTER_STATE } from 'FoldersApp/apps/compliance-report-history-app/state/compliance-report-type/compliance-report-type.reducer';

import { DESC } from 'Angular/constants/core.constants';
import { ComplianceReportHistoryPageSortColumn } from 'FoldersApp/apps/compliance-report-history-app/constants';
import { COMPLIANCE_REPORT_HISTORY_PARAM_KEYS } from 'FoldersApp/apps/compliance-report-history-app/state/constants';
import {
    NotificationCategory,
    TODO_NOTIFICATION_TYPES,
} from 'NotificationsApp/ts/features/notifications-side-menu/constants';
import { ComplianceReportCollectionViewResponse } from 'FoldersApp/apps/compliance-report-history-app/interfaces/compliance-report-collection-view-response.interface';
import { ComplianceReport } from 'FoldersApp/apps/compliance-report-history-app/interfaces/compliance-report-data.interface';
import { ComplianceReportParams } from 'FoldersApp/apps/compliance-report-history-app/interfaces/compliance-report-params.interface';
// Interfaces
import { Notification } from 'NotificationsApp/ts/interfaces/notifications.interface';

// Services
import { AccountsService } from 'AccountsApp/ts/services/accounts.service';
import { UrlsService } from 'Angular/services/core/urls.service';
import { ComplianceReportService } from 'FoldersApp/apps/compliance-report-history-app/services/compliance-report.service';
import { NotificationsService } from 'NotificationsApp/ts/services/notifications.service';

import { formFlattenedObject } from 'Angular/utils/urls';

type NotificationsByCategory = Record<NotificationCategory, Notification[]>;

@Component({
    standalone: false,
    selector: 'ycn-notifications-side-menu',
    templateUrl: './notifications-side-menu.template.html',
})
export class NotificationsSideMenuComponent implements AfterViewInit, OnDestroy, OnInit {
    @ViewChild('regionsContainer') regionsContainer!: ElementRef<HTMLElement>;

    @ViewChild('notificationsScrollContainer') notificationsScrollContainer!: ElementRef<HTMLElement>;

    @ViewChild('notificationsScrollTarget') notificationsScrollTarget!: ElementRef<HTMLElement>;

    @ViewChild('reportsScrollContainer') reportsScrollContainer!: ElementRef<HTMLElement>;

    @ViewChild('reportsScrollTarget') reportsScrollTarget!: ElementRef<HTMLElement>;

    readonly topSectionPercentage = model<number>(65);

    loading: boolean = false;

    loadingReports: boolean = false;

    readonly notifications = model<Notification[]>([]);

    notificationsByCategory: NotificationsByCategory = {
        activity: [],
        todo: [],
    };

    activeTab: NotificationCategory = 'activity';

    reportHistoryUrl: string = '';

    complianceReportFetchParams: ComplianceReportParams = formFlattenedObject(
        {
            endDate: null,
            reportType: INITIAL_COMPLIANCE_REPORT_TYPE_FILTER_STATE,
            designationType: INITIAL_COMPLIANCE_DESIGNATION_TYPE_FILTER_STATE,
            startDate: null,
            sortColumn: ComplianceReportHistoryPageSortColumn.DOWNLOAD_DATE,
            sortDirection: DESC,
            title: '',
            owner: { me: true, shared_with_me: true },
        },
        COMPLIANCE_REPORT_HISTORY_PARAM_KEYS
    );

    reports: ComplianceReport[] = [];

    isDragging = false;

    private readonly MAX_REGION_PERCENT = 85;

    private readonly MIN_REGION_PERCENT = 15;

    // Tolerance for sub-pixel rounding — scrollTop + clientHeight can land a fraction below
    // scrollHeight when scrolled to the very bottom, which would otherwise leave the shadow on.
    private readonly SCROLL_SHADOW_THRESHOLD = 1;

    private resizeObserver: ResizeObserver | null = null;

    private scrollShadowAnimationFrame: number | null = null;

    constructor(
        private notificationsService: NotificationsService,
        private complianceReportService: ComplianceReportService,
        private accountsService: AccountsService,
        private urlsService: UrlsService,
        private renderer: Renderer2
    ) {
        this.reportHistoryUrl = this.urlsService.FOLDERS.MY_YCHARTS_REPORT_HISTORY;

        effect(() => {
            this.notifications();
            this.setNotificationsByCategory();
        });
    }

    ngOnInit(): void {
        this.setNotificationsByCategory();
        this.fetchComplianceReports();
    }

    ngAfterViewInit(): void {
        this.resizeObserver = new ResizeObserver(() => this.scheduleScrollShadowUpdate());
        this.resizeObserver.observe(this.regionsContainer.nativeElement);
        this.scheduleScrollShadowUpdate();
    }

    ngOnDestroy(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.scrollShadowAnimationFrame !== null) {
            cancelAnimationFrame(this.scrollShadowAnimationFrame);
        }
    }

    deleteAllNotifications(): void {
        this.loading = true;
        this.notificationsService
            .deleteNotifications()
            .pipe(
                finalize(() => {
                    this.loading = false;
                })
            )
            .subscribe({
                next: () => {
                    this.notifications.set([]);
                    this.setNotificationsByCategory();
                },
                error: () => {},
            });
    }

    removeNotification(notification: Notification): void {
        this.notifications.set(this.notifications().filter((n) => n.id !== notification.id));
        this.setNotificationsByCategory();
    }

    markNotificationRead(notification: Notification): void {
        this.notifications.set(
            this.notifications().map((n) =>
                n.id === notification.id && n.type === notification.type ? { ...n, read: true } : n
            )
        );
        this.setNotificationsByCategory();
    }

    selectTab(tabType: NotificationCategory): void {
        this.activeTab = tabType;
        this.scheduleScrollShadowUpdate();
    }

    setNotificationsByCategory(): void {
        this.notificationsByCategory = this.notifications().reduce(
            (acc, notification) => {
                const category = TODO_NOTIFICATION_TYPES.has(notification.type) ? 'todo' : 'activity';
                acc[category].push(notification);
                return acc;
            },
            { activity: [], todo: [] } as NotificationsByCategory
        );
        this.scheduleScrollShadowUpdate();
    }

    fetchComplianceReports(): void {
        this.loadingReports = true;
        this.complianceReportService
            .getComplianceReports({ ...this.complianceReportFetchParams, page: 1 })
            .pipe(
                finalize(() => {
                    this.loadingReports = false;
                })
            )
            .subscribe({
                next: (rsp: ComplianceReportCollectionViewResponse) => {
                    this.reports = rsp.data.items.slice(0, 7);
                    this.scheduleScrollShadowUpdate();
                },
                error: () => {},
            });
    }

    onPointerDown(event: PointerEvent): void {
        event.preventDefault();
        this.isDragging = true;
        (event.target as HTMLElement).setPointerCapture(event.pointerId);
    }

    onPointerMove(event: PointerEvent): void {
        if (!this.isDragging) return;

        const containerRect = this.regionsContainer.nativeElement.getBoundingClientRect();
        const newTopSectionPercentage = Math.min(
            this.MAX_REGION_PERCENT,
            Math.max(this.MIN_REGION_PERCENT, ((event.clientY - containerRect.top) / containerRect.height) * 100)
        );

        this.topSectionPercentage.set(newTopSectionPercentage);
        this.scheduleScrollShadowUpdate();
    }

    onPointerUp(event: PointerEvent): void {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.accountsService
            .putUserPreferences({ side_menu_top_section_percentage: this.topSectionPercentage() })
            .subscribe();
        (event.target as HTMLElement).releasePointerCapture(event.pointerId);
    }

    scrollShadow(element: HTMLElement, targetElement: HTMLElement): void {
        const elemHeight = element.scrollHeight;
        this.renderer.removeClass(targetElement, 'scrolled-bottom');
        this.renderer.removeClass(targetElement, 'scrolled-top');
        if (element.scrollTop > this.SCROLL_SHADOW_THRESHOLD) {
            this.renderer.addClass(targetElement, 'scrolled-bottom');
        }
        if (element.scrollTop + element.clientHeight < elemHeight - this.SCROLL_SHADOW_THRESHOLD) {
            this.renderer.addClass(targetElement, 'scrolled-top');
        }
    }

    private scheduleScrollShadowUpdate(): void {
        if (!this.notificationsScrollContainer || !this.reportsScrollContainer) return;

        if (this.scrollShadowAnimationFrame !== null) {
            cancelAnimationFrame(this.scrollShadowAnimationFrame);
        }
        this.scrollShadowAnimationFrame = requestAnimationFrame(() => {
            this.scrollShadowAnimationFrame = null;
            this.updateScrollShadows();
        });
    }

    private updateScrollShadows(): void {
        this.scrollShadow(
            this.notificationsScrollContainer.nativeElement,
            this.notificationsScrollTarget.nativeElement
        );
        this.scrollShadow(this.reportsScrollContainer.nativeElement, this.reportsScrollTarget.nativeElement);
    }
}
